import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Grid
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { gradient, shadow } from '../../utils/darkModeAdapter';
import HistoryIcon from '@mui/icons-material/History';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import SchoolIcon from '@mui/icons-material/School';

export default function AssistMenu({
  onResetClick,
  onRegenerateClick,
  onDecideJobPost,
  onShowHistory,
  requirements = {},
  jobPostMd = '',
  jobPostHistory = [],
  currentHistoryIndex = -1,
  onHistorySelect,
  onPrevVersion,
  onNextVersion,
  anchorEl,
  onClose
}) {
  const theme = useTheme();
  const open = Boolean(anchorEl);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  // メニュー項目の共通スタイル
  const menuItemStyle = (color = '#fc67fa') => ({
    '&:hover': {
      backgroundColor: alpha(color, 0.1),
    },
    padding: '12px 16px',
    margin: '2px 0',
    borderRadius: '8px',
    '& .MuiListItemIcon-root': {
      color: color,
      minWidth: '40px',
    },
    '& .MuiListItemText-primary': {
      fontWeight: 'bold',
      fontSize: '0.95rem',
    }
  });

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleHistoryClick = () => {
    handleClose();
    if (onShowHistory) {
      onShowHistory();
    }
  };

  const handleDecideJobPostClick = () => {
    handleClose();
    setConfirmationDialogOpen(true);
  };

  const handleConfirmDecideJobPost = () => {
    setConfirmationDialogOpen(false);
    if (onDecideJobPost) onDecideJobPost();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            boxShadow: shadow(theme).lg,
            minWidth: 220,
            padding: '8px',
            background: gradient(theme).menu,
          },
        }}
      >
        <MenuItem onClick={onResetClick} sx={menuItemStyle('#f44336')}>
          <ListItemIcon>
            <RestartAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>初めから作成</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1, opacity: 0.6 }} />

        <MenuItem onClick={onPrevVersion} disabled={!onPrevVersion} sx={menuItemStyle('#48c6ef')}>
          <ListItemIcon>
            <ArrowBackIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>前のバージョン</ListItemText>
        </MenuItem>

        <MenuItem onClick={onNextVersion} disabled={!onNextVersion} sx={menuItemStyle('#48c6ef')}>
          <ListItemIcon>
            <ArrowForwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>次のバージョン</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1, opacity: 0.6 }} />

        <MenuItem onClick={handleHistoryClick} sx={menuItemStyle('#4caf50')}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>過去の出力履歴を表示</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1, opacity: 0.6 }} />

        <MenuItem onClick={handleDecideJobPostClick} sx={menuItemStyle('#6f86d6')}>
          <ListItemIcon>
            <DoneOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>求人票を確定する</ListItemText>
        </MenuItem>
      </Menu>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box
          sx={{
            pb: 1,
            background: 'linear-gradient(45deg, #6f86d6 0%, #48c6ef 100%)',
            color: 'white',
            px: 3,
            py: 2,
          }}
        >
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            求人票を確定しますか？
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" paragraph>
            確定すると掲載準備が完了し、選定した媒体への配信がスムーズに進められます。
            確定後も必要に応じて内容の修正が可能です。
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <WorkIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    職種
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {requirements.role || '未設定'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SchoolIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    必要スキル
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {requirements.skills || '未設定'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PaymentIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    給与
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {requirements.salary || '未設定'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOnIcon sx={{ mr: 1, color: 'error.main' }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    勤務地
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {requirements.location || '未設定'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmationDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: '20px' }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDecideJobPost}
            variant="contained"
            sx={{
              borderRadius: '20px',
              background: 'linear-gradient(45deg, #4caf50 0%, #45a049 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, #45a049 0%, #3d8b40 100%)',
              }
            }}
          >
            確定する
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
